/**
 * StarRating — 5-star selector with optional N/A and contextual explanations.
 *
 * Props:
 *   questionKey  string  — maps to QUESTION_EXPLANATIONS for contextual text
 *   value        string  — '1'–'5', 'N/A', or ''
 *   onChange     fn(val) — called when user selects a value
 *   error        string  — inline validation message
 */

const QUESTION_EXPLANATIONS = {
  time_efficiency: {
    '1': '✗ Using AI significantly increased time needed',
    '2': '✕ Using AI increased some time needed',
    '3': '◐ Neutral — no noticeable time impact',
    '4': '✓ Some time was saved',
    '5': '✓✓ Significant amount of time was saved',
    'N/A': '⊘ Not applicable to this phase',
  },
  quality_output: {
    '1': '✗ Output quality was significantly worse',
    '2': '✕ Output quality was somewhat worse',
    '3': '◐ No noticeable change in quality',
    '4': '✓ Output quality improved somewhat',
    '5': '✓✓ Output quality significantly improved',
    'N/A': '⊘ Not applicable to this phase',
  },
  clarity_detail: {
    '1': '✗ Clarity and detail were significantly worse',
    '2': '✕ Clarity and detail were somewhat worse',
    '3': '◐ No noticeable change in clarity',
    '4': '✓ Clarity and detail improved somewhat',
    '5': '✓✓ Clarity and detail significantly improved',
    'N/A': '⊘ Not applicable to this phase',
  },
  risk_prevention: {
    '1': '✗ AI introduced significant new risks',
    '2': '✕ AI introduced some additional risks',
    '3': '◐ No noticeable change in risk profile',
    '4': '✓ Some risks were reduced or prevented',
    '5': '✓✓ Significant risk reduction or prevention',
    'N/A': '⊘ Not applicable to this phase',
  },
  value_collaboration: {
    '1': '✗ AI significantly reduced collaboration value',
    '2': '✕ AI reduced collaboration value somewhat',
    '3': '◐ No noticeable change in collaboration',
    '4': '✓ Collaboration value improved somewhat',
    '5': '✓✓ Collaboration value significantly improved',
    'N/A': '⊘ Not applicable to this phase',
  },
  repeat_refine: {
    '1': '✗ Would definitely not use AI for this again',
    '2': '✕ Would likely not use AI for this again',
    '3': '◐ Undecided — might use again',
    '4': '✓ Would likely use AI for this again',
    '5': '✓✓ Would definitely use AI for this again',
    'N/A': '⊘ Not applicable to this phase',
  },
}

const GENERIC_EXPLANATIONS = {
  '1': '✗ Strongly negative impact',
  '2': '✕ Somewhat negative impact',
  '3': '◐ Neutral — no noticeable impact',
  '4': '✓ Somewhat positive impact',
  '5': '✓✓ Strongly positive impact',
  'N/A': '⊘ Not applicable to this phase',
}

export default function StarRating({ questionKey, value, onChange, error }) {
  const explanations = QUESTION_EXPLANATIONS[questionKey] ?? GENERIC_EXPLANATIONS
  const explanation = value ? (explanations[value] ?? '') : ''

  return (
    <div className="star-rating">
      <div className="star-rating-buttons" role="group" aria-label="Rating">
        {['1', '2', '3', '4', '5'].map((v) => (
          <button
            key={v}
            type="button"
            className={`star-btn${value === v ? ' selected' : ''}`}
            onClick={() => onChange(v)}
            aria-label={`${v} star${v !== '1' ? 's' : ''}`}
            aria-pressed={value === v}
          >
            ★
          </button>
        ))}
        <button
          type="button"
          className={`star-btn na-btn${value === 'N/A' ? ' selected' : ''}`}
          onClick={() => onChange('N/A')}
          aria-label="Not applicable"
          aria-pressed={value === 'N/A'}
        >
          N/A
        </button>
      </div>

      <p className="star-explanation">{explanation}</p>

      {error && <span className="field-error">{error}</span>}
    </div>
  )
}
